export const TABLET_MIN_WIDTH_PX = 768;
export const PC_MIN_WIDTH_PX = 1020;

export const zIndex = {
  initial: 1,
  header: 110,
  menu: 100,
  filter: 120,
};

export const mediaQureyWidthPairs = {
  pc: { min: `${PC_MIN_WIDTH_PX}px` },
  tablet: { min: `${TABLET_MIN_WIDTH_PX}px`, max: `${PC_MIN_WIDTH_PX - 1}px` },
  sp: { max: `${TABLET_MIN_WIDTH_PX - 1}px` },
};

export type MediaQureyWidthPairs = typeof mediaQureyWidthPairs;

export const mediaQurey = <
  T extends keyof MediaQureyWidthPairs,
  S extends keyof MediaQureyWidthPairs
>(
  minMax: keyof MediaQureyWidthPairs[T],
  deviceType: T,
  minMax2?: keyof MediaQureyWidthPairs[S],
  deviceType2?: S,
) =>
  `@media (${String(minMax)}-width: ${mediaQureyWidthPairs[deviceType][minMax]})${
    (minMax2 && deviceType2)
      ? ` and (${String(minMax2)}-width: ${mediaQureyWidthPairs[deviceType2][minMax2]})`
      : ''
  }`;

export const generalStyle = {
  list: <
    Align extends 'left' | 'center' | 'right',
    ListOption extends { default: Align, left: number[], center: number[], right: number[] }
  >(
    length: number,
    option?: Partial<ListOption>
  ) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${length}, auto)`,
    fontSize: '1.6rem',
    ...(option?.default ? { justifyContent: option.default } : {}),
    ...(['left', 'center', 'right'] as Align[])
      .reduce((params, align) => ({
        ...params,
        ...((option?.[align] || []).reduce((param, column) => ({
          ...param,
          [`& > div:nth-of-type(n+2) > div:nth-of-type(${column})`]: ({ justifyContent: align }),
          [`& > label > div:nth-of-type(${column})`]: ({ justifyContent: align }),
        }), {})),
      }), {}),
  }),
  listRow: {
    display: 'contents',
    ['& > div']: {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem',
      borderBottom: 'solid 0.1rem #ccc',
    },
    ['&:nth-child(1) > div']: {
      flexDirection: 'column' as const,
      justifyContent: 'center',
      alignItems: 'flex-start',
      fontSize: '1.2rem',
      fontWeight: '700',
      ['& > span']: {
        display: 'inline-block',
      },
    },
    ['&:nth-child(n+2):hover > div']: {
      backgroundColor: '#f0f0f0',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
    ['& input[type="text"],input[type="number"],input[type="password"]']: {
      width: '100%',
      border: 'none',
      borderBottom: 'solid 0.1rem #cccccc',
      background: 'none !important',
      lineHeight: '2.4rem',
      fontSize: '1.6rem',
      '&:internal-autofill-selected': {
        backgroundColor: '#ffffff !important',
      },
    },
    ['& textarea']: {
      minHeight: '10rem',
      padding: '0.4rem 0.8rem',
      lineHeight: '1.4',
      fontSize: '1.6rem',
    },
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  /**
   * List コンポーネントを含む領域に付ける。
   * mainArea (flex column) の中で残り高さを占有し、List 内部スクロールを成立させる。
   */
  listLayout: {
    flex: '1 1 auto' as const,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  inputArea: {
    display: 'flex',
    gap: '0.5rem',
    borderBottom: 'solid 0.1rem #cccccc',
    ['& input[type="text"],input[type="number"],input[type="password"]']: {
      borderBottom: 'none !important',
    },
  },
  button: (type: 'normal' | 'info' | 'warning' = 'normal', disabled = false) => ({
    display: 'inline-block',
    padding: '0.8rem 2rem',
    border: 'none',
    borderRadius: '0.5rem',
    boxShadow: '0 0.1rem 0.2rem #00000088',
    color: '#ffffff',
    textDecoration: 'none',
    fontSize: '1.6rem',
    ...(
      disabled
        ? {
          backgroundColor: '#aaaaaa',
          cursor: 'auto',
        }
        : {
          backgroundColor: {
            normal: '#e9ba32',
            info: '#8888ff',
            warning: '#aa6666',
          }[type],
          cursor: 'pointer',
          ['&:hover']: {
            backgroundColor: {
              normal: '#e0b030',
              info: '#4444aa',
              warning: '#880000',
            }[type],
          },
        }
    ),
  }),
  item: (option?: Partial<{ align: 'left' | 'center' | 'right' }>) => ({
    borderBottom: 'solid 0.1rem #cccccc',
    lineHeight: '2.4rem',
    minHeight: '2.4rem',
    whiteSpace: 'pre-wrap',
    ...(option?.align ? { textAlign: option.align } : {}),
  }),
  link: {
    color: '#073f99',
    textDecoration: 'none',
    ['&:hover']: {
      textDecoration: 'underline',
    },
  },
  onlyPc: {
    [mediaQurey('max', 'tablet')]: {
      display: 'none !important',
    },
  },
  nonPc: {
    [mediaQurey('min', 'pc')]: {
      display: 'none !important',
    },
  },
};
